import React, { useState, useEffect } from 'react';
import Layout from '../../layout/Layout';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import axios from 'axios';
import InfinitePagination from '../../modules/InfinitePagination';
import BlogRoll from '../../components/blog/BlogRoll';
import { PersonIcon, CalendarIcon } from '../../modules/Icons';
import moment from 'moment';
import ProgressBar from '../../components/shared/ProgressBar';
import BlogNavigation from '../../components/blog/BlogNavigation';
import { InternalAdsContainer } from '../../components/shared/InternalAd';
import { MainLinkButton } from '../../components/styled/buttons/MainLinkButton';
import errorRedirectHandler from '../../helpers/errorRedirectHandler';
import { seoBlog } from '../../staticData/Seo';
import { getCategories, getPosts, getRootCategories } from '../../helpers/pages/blog/blogHelpers';

const BlogIndex: React.FC<AppProps> = (props: any) => {
  const { totalPages, categories, activeCategory, searchQuery } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState(props.articles);

  const [featuredImageIndex, setFeaturedImageIndex] = useState(0);
  const [currentPercent, setCurrentPercent] = useState(0);
  const [firstArticle, setFirstArticle] = useState(articles[0]);
  const showFeatured = articles.length > 5 && searchQuery == '';
  const blogRollArticles = showFeatured ? articles.slice(3) : articles;

  const router = useRouter();

  const switchFeaturedImage = (k = null) => {
    if (k !== null) {
      setFirstArticle(articles[k]);
      setFeaturedImageIndex(k);
      return;
    }

    if (featuredImageIndex == 2) {
      setFirstArticle(articles[0]);
      setFeaturedImageIndex(0);
      return;
    }
    setFirstArticle(articles[featuredImageIndex + 1]);
    setFeaturedImageIndex(featuredImageIndex + 1);
  };

  useEffect(() => {
    setCurrentPercent(100);
    const interval = setInterval(() => switchFeaturedImage(), 10000);
    return () => clearInterval(interval);
  }, [firstArticle]);

  /**
   * @todo When we have more than 15 articles, we must upgrade this function to work with Storyblok
   */


  const getArticles = async (increment: boolean = false) => {
    let pageNum: number = currentPage || 1;
    if (increment) pageNum++;

    let params = {
      page: pageNum
    };

    axios
      .get(
        `/api/blog?q=${searchQuery || ''}&category=${activeCategory?.slug || ''}&page=${pageNum}`
      )
      .then((response) => {
        setArticles(articles.concat(response.data.articles));
        setCurrentPage(pageNum);
      });
  };

  const dynamicSeo = seoBlog.find((item) => item.path === router.asPath);

  const headContent = activeCategory?.title
    ? {
        title: activeCategory.title + ' - Blog',
        canonical: `blog?category=${activeCategory.slug}`,
        description: dynamicSeo?.description
      }
    : {
        title: 'Blog',
        canonical: 'blog',
        description: dynamicSeo?.description
      };

  return (
    <Layout head={headContent}>
      <BlogNavigation
        activeCategory={activeCategory}
        categories={categories}
        searchQuery={searchQuery}
      />

      {showFeatured && (
        <div className="">
          <article className="bg-gray-1000 rounded-[5px] overflow-hidden">
            <div className="w-full md:h-[660px] overflow-hidden">
              <div
                className={`w-full py-8 md:h-[660px] bg-center bg-cover flex items-center relative`}
                style={{
                  backgroundImage: `url(${firstArticle?.header_photo?.url})`,
                  imageRendering: '-webkit-optimize-contrast'
                }}>
                <div className="absolute top-0 left-0 w-full h-full blog-header-img"></div>
                <div className="blog-header-gradient"></div>
                <div className="relative z-20 w-full">
                  <div className="flex flex-col md:flex-row gap-4 lg:gap-6">
                    <div className="mx-8 mt-5 lg:mt-40 md:mx-12 lg:mx-16 max-w-[660px] lg:min-h-[600px]">
                      <div className="flex gap-8 text-white-60 uppercase text-[10px] md:text-[12px] pt-4">
                        <div className="">
                          <CalendarIcon className="inline mr-1" />{' '}
                          {moment(firstArticle.publish_date).format('MMMM D, YYYY')}
                        </div>

                        {firstArticle.author && (
                          <div className="">
                            <PersonIcon className="inline w-4 fill-gray-500 mr-1" />
                            {firstArticle.author.name}
                          </div>
                        )}
                      </div>
                      <h2 className="inter font-bold text-[28px] md:text-[44px] white-95 md:leading-[56px] mt-5">
                        {firstArticle.title}
                      </h2>
                      <p className="text-white-80 text-[14px] md:text-[18px] md:leading-[32px] mt-5 mb-8">
                        {firstArticle.excerpt}
                      </p>

                      <MainLinkButton
                        href={`/blog/${firstArticle.slug}`}
                        className="whitespace-nowrap !mb-5"
                        inline={true}>
                        Read Post
                      </MainLinkButton>
                    </div>

                    <div className="hidden md:block ml-auto lg:w-[320px] mr-8 lg:mr-36 lg:mt-40">
                      {articles.slice(0, 3).map((article, k) => (
                        <div
                          className="py-4 cursor-pointer"
                          key={k}
                          onClick={() => {
                            switchFeaturedImage(k);
                          }}>
                          <ProgressBar
                            progressPct={k == featuredImageIndex ? currentPercent : 0}
                            bgColor="bg-a-darkgreen-10 h-[5px]"
                            className={`bg-a-green transition-width ${
                              featuredImageIndex == k ? 'duration-[10s]' : ''
                            } `}
                          />
                          <h3 className="inter text-[18px] tracking-[0.03em] text-white-50">
                            {article.title}
                          </h3>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="md:hidden flex items-center justify-start gap-6 ml-8 mt-12">
                    {articles.slice(0, 3).map((article, k) => (
                      <div
                        className={`${
                          k == featuredImageIndex ? 'bg-a-blue' : 'bg-white-50'
                        } w-3 h-3 rounded-full`}
                        key={k}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      )}

      <div className="py-8 bg-gray-900">
        {searchQuery && (
          <p className="text-gray-600 text-[18px] my-6 px-8 md:px-16 2xl:px-36">
            Search results for <strong className="font-bold text-white">{searchQuery}</strong>
          </p>
        )}
        {articles.length == 0 && (
          <p className="text-white text-[14px] text-center py-8">
            There are no blog posts matching your filters. Please search using a different query or
            filter by categories.
          </p>
        )}

        {activeCategory && (
          <h1 className="text-[28px] my-6 pb-8 text-center eurostile uppercase font-bold tracking-wider">
            {activeCategory.title}
          </h1>
        )}

        <BlogRoll articles={blogRollArticles} page={currentPage} />
        <InfinitePagination
          totalPages={999}
          currentPage={currentPage}
          loadHref={`/blog/?q=${searchQuery}&category=${activeCategory?.slug}&page=[page]`}
          loadAs={`/blog?q=${searchQuery}&category=${activeCategory?.slug}&page=$`}
          getContent={getArticles}
        />

      </div>
      <InternalAdsContainer placement="blog_main" height="360" />
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  ctx.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=59');

  const cmsApi: string = process.env.CMS_API_URL;
  const cmsApiKey: string = process.env.CMS_API_KEY;
  const { category, q } = ctx.query;

  let categoriesRes: any;

  /**
   * @todo Migrate this to Storyblok somehow
   */
  try {
    categoriesRes = await getCategories();
  } catch (error) {
    return errorRedirectHandler(error);
  }

  const categories = categoriesRes;
  const selectedCategory = categories.find((c) => c.slug == category);

  let contentRes: any;
  let articles: any;

  try {
    articles = await getPosts(contentRes, selectedCategory, q);
  } catch (error) {
    return errorRedirectHandler(error);
  }

  let rootCategoriesRes: any;
  try {
    rootCategoriesRes = await getRootCategories();
  } catch (error) {
    return errorRedirectHandler(error);
  }

  return {
    props: {
      totalPages: contentRes?.data?.total_pages || 1,
      articles: articles,
      categories: rootCategoriesRes,
      activeCategory: selectedCategory || {},
      searchQuery: q || false
    }
  };
};

export default BlogIndex;
